// Unpublished Work © 2022-2026 Deere & Company.

import PropTypes from 'Utils/prop-type-utils';
import React from 'react';
import {dataTableFormatTime} from 'Utils/time-utils';
import {formatNumber} from 'Utils/unit-conversion-utils';
import {BY_EQUIPMENT_TYPE, BY_MODEL} from 'Ui/components/graph/constants/graph-filters';
import {UNITS} from 'Common/constants/units/unit-config-constants';
import {isNullOrUndefined} from 'Common/utils/validation-utils';
import DataGridWrapper from 'Ui/components/common/data-table/data-grid-wrapper';
import {isAggregationFooterRow} from 'Ui/components/common/data-table/data-grid-utils';

function getFormattedRowValue(value) {
    return formatNumber(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

export function getHeaderColumnName(secondarySelector, translations) {
    switch (secondarySelector) {
        case BY_EQUIPMENT_TYPE:
            return translations.ONLINK_EQUIPMENT_TYPE;
        case BY_MODEL:
            return translations.MODEL;
        default:
            return translations.IWP_EQUIPMENT_LABEL;
    }
}

function getDataGridColumns(secondarySelector, translations, uptimeReportData) {
    const totals = uptimeReportData.totals;
    const hasTotals = !isNullOrUndefined(totals);

    let footerDateLabel = '';

    if (hasTotals) {
        const formattedDate = dataTableFormatTime(totals.dateSelect, totals.dateScale);

        footerDateLabel = `${formattedDate} ${translations.ONLINK_TOTAL}`;
    }

    return [
        {
            headerName: getHeaderColumnName(secondarySelector, translations),
            field: 'name',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                if (isAggregationFooterRow(params)) {
                    return footerDateLabel;
                }

                return params.value;
            }
        },
        {
            headerName: translations.ONLINK_UPTIME,
            field: 'uptime',
            minWidth: 150,
            flex: 1,
            type: 'number',
            valueFormatter: (value) => getFormattedRowValue(value),
            renderCell: (params) => {
                if (isAggregationFooterRow(params)) {
                    return hasTotals ? getFormattedRowValue(totals.uptime) : '';
                }

                return getFormattedRowValue(params.value);
            }
        },
        {
            headerName: translations.ONLINK_DOWNTIME,
            field: 'downtime',
            minWidth: 150,
            flex: 1,
            type: 'number',
            valueFormatter: (value) => getFormattedRowValue(value),
            renderCell: (params) => {
                if (isAggregationFooterRow(params)) {
                    return hasTotals ? getFormattedRowValue(totals.downtime) : '';
                }

                return getFormattedRowValue(params.value);
            }
        },
        {
            headerName: translations.ONLINK_PERCENT,
            field: 'ratio',
            minWidth: 150,
            flex: 1,
            type: 'number',
            valueFormatter: (value) => `${getFormattedRowValue(value)} ${UNITS.percent}`,
            renderCell: (params) => {
                if (isAggregationFooterRow(params)) {
                    return hasTotals ? `${getFormattedRowValue(totals.ratio)} ${UNITS.percent}` : '';
                }

                return `${getFormattedRowValue(params.value)} ${UNITS.percent}`;
            }
        }
    ];
}

function UptimeReportDataTable(props) {
    const {
        secondarySelector,
        translations,
        uptimeReportData
    } = props;

    return (
        <div className={'ops-center-table-container'}>
            <DataGridWrapper
                aggregation={{
                    name: {
                        type: 'size',
                        config: {}
                    },
                    uptime: {
                        type: 'sum',
                        config: {}
                    },
                    downtime: {
                        type: 'sum',
                        config: {}
                    },
                    ratio: {
                        type: 'avg',
                        config: {}
                    }
                }}
                columns={getDataGridColumns(secondarySelector, translations, uptimeReportData)}
                getRowId={(row) => row.name ?? translations.ONLINK_TOTAL}
                pinnedColumns={{
                    left: [],
                    right: []
                }}
                rows={uptimeReportData.rollupsForSelector}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

UptimeReportDataTable.propTypes = {
    secondarySelector: PropTypes.string,
    translations: PropTypes.translations,
    uptimeReportData: PropTypes.object
};

export default UptimeReportDataTable;
